import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { url: String }
  static targets = ["results", "resultsList"]

  connect() {
    this.timeout = null
    console.log("Dealer search controller connected")
    console.log("Results target available:", this.hasResultsTarget)
    console.log("ResultsList target available:", this.hasResultsListTarget)
    console.log("All targets:", this.targets)
    
    // Initialize error notification system
    this.initErrorNotifications()
    
    // Try to find targets manually for debugging
    const resultsEl = document.querySelector('[data-dealer-search-target="results"]')
    const resultsListEl = document.querySelector('[data-dealer-search-target="resultsList"]')
    console.log("Manual DOM query - results element:", !!resultsEl)
    console.log("Manual DOM query - resultsList element:", !!resultsListEl)
  }

  search(event) {
    const query = event.target.value.trim()
    
    // Clear existing timeout
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
    
    // Debounce the search
    this.timeout = setTimeout(() => {
      if (query.length >= 2) {
        this.performSearch(query)
      } else if (query.length === 0) {
        this.hideResults()
      }
    }, 300)
  }

  async performSearch(query) {
    try {
      const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        this.displayResults(data.sales_reps, query)
      }
    } catch (error) {
      console.error('Error searching dealers:', error)
      this.showConnectionError({
        operation: 'searching for dealers',
        technical: error.message
      }, () => this.searchDealers())
    }
  }

  displayResults(salesReps, query) {
    console.log("displayResults called with:", salesReps.length, "results")
    
    // Check if both required targets are available
    if (!this.hasResultsListTarget) {
      console.error("ResultsList target not found - check HTML data-dealer-search-target='resultsList'")
      return
    }
    
    if (!this.hasResultsTarget) {
      console.error("Results target not found - check HTML data-dealer-search-target='results'")
      return
    }
    
    if (salesReps.length === 0) {
      this.resultsListTarget.innerHTML = `
        <div class="text-center py-4">
          <p class="text-sm text-gray-500">No dealers found near "${query}"</p>
          <p class="text-xs text-gray-400 mt-1">Try searching for a city, state, or zip code</p>
        </div>
      `
    } else {
      this.resultsListTarget.innerHTML = this.buildResultsHTML(salesReps)
    }
    
    this.showResults()
  }

  buildResultsHTML(salesReps) {
    return salesReps.map(rep => `
        <div class="border rounded-lg p-4 hover:bg-gray-50 transition-colors duration-200">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <div class="flex-shrink-0 h-10 w-10">
                <div class="h-10 w-10 rounded-full bg-gradient-to-br from-blue-100 to-emerald-100 flex items-center justify-center shadow-sm">
                  <span class="text-sm font-semibold text-blue-700">${rep.name.charAt(0)}</span>
                </div>
              </div>
              <div class="ml-3">
                <div class="text-sm font-medium text-gray-900">${rep.name}</div>
                <div class="text-xs text-gray-500">${rep.email}</div>
                ${rep.phone_number ? `
                  <div class="text-xs text-gray-600 mt-1">
                    <a href="tel:${rep.phone_number}" class="inline-flex items-center hover:text-blue-600 transition-colors">
                      <svg class="inline w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                      </svg>
                      ${rep.phone_number}
                    </a>
                  </div>
                ` : ''}
                ${rep.location ? `
                  <div class="text-xs text-blue-600 mt-1">
                    <svg class="inline w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                    </svg>
                    ${rep.location}
                    ${rep.distance_miles ? `<span class="ml-2 text-emerald-600 font-medium">• ${rep.distance_miles} miles</span>` : ''}
                  </div>
                ` : ''}
                ${rep.orders_count > 0 ? `
                  <div class="text-xs text-emerald-600 mt-1">
                    <svg class="inline w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                    </svg>
                    Active Sales Representative
                  </div>
                ` : ''}
              </div>
            </div>
            <div>
              <a href="${rep.website_url}" target="_blank" rel="noopener" class="inline-flex items-center px-3 py-2 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200">
                Visit Website
                <svg class="ml-1 w-3 h-3" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      `).join('')
  }

  showResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.remove('hidden')
    }
  }

  hideResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add('hidden')
    }
  }
  
  // Target connection callbacks for debugging
  resultsTargetConnected(element) {
    console.log("Results target connected:", element)
  }
  
  resultsListTargetConnected(element) {
    console.log("ResultsList target connected:", element)
  }
  
  resultsTargetDisconnected(element) {
    console.log("Results target disconnected:", element)
  }
  
  resultsListTargetDisconnected(element) {
    console.log("ResultsList target disconnected:", element)
  }

  // Initialize error notification system
  initErrorNotifications() {
    this.errorNotificationController = this.application.getControllerForElementAndIdentifier(
      document.body, 'error-notification'
    )
  }

  // Error handling methods
  showConnectionError(context, retryCallback) {
    this.errorNotificationController?.showConnectionError(context, retryCallback)
  }
}
